import React from "react";

import { useDeviceRegistrationGroup } from "@dashboard/shared/hooks/useDeviceRegistrationGroup";

import GenerateQRCodeDialog from "./GenerateQRCodeDialog";
import * as Utils from "./GenerateQRCodeDialog.utils";

const createInitialValuesUtil = (licenseCode: string): Utils.FormValues => {
    return {
        ...Utils.initialValues,
        ["android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE"]: licenseCode ? { UL: licenseCode } : undefined,
    };
};

interface Props {
    isOpen: boolean;
    groupId: number;
    onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const GenerateQRCodeDialogWrapper = (props: Props) => {
    const { ensureUniversalLicense, getUniversalLicenseCodeForGroup } = useDeviceRegistrationGroup();

    // The activation code is kept together with the group it belongs to. Storing the bare code meant that
    // switching the panel to another group kept showing the previous group's code while the new one was still
    // being looked up, so a device scanned in that moment enrolled into the wrong group. Pairing them also makes
    // a late answer for the previous group harmless: it simply no longer matches and nothing is rendered.
    const [license, setLicense] = React.useState<{ groupId: number; code: string } | null>(() => {
        const code = getUniversalLicenseCodeForGroup(props.groupId);
        return code ? { groupId: props.groupId, code } : null;
    });

    React.useEffect(() => {
        if (!props.isOpen) {
            return;
        }

        ensureUniversalLicense(props.groupId).then((code) => {
            if (code) {
                setLicense({ groupId: props.groupId, code });
            }
        });
    }, [props.groupId, props.isOpen, ensureUniversalLicense]);

    const licenseCode = license?.groupId === props.groupId ? license.code : undefined;

    if (!licenseCode) {
        return null;
    }

    return (
        <GenerateQRCodeDialog
            isOpen={props.isOpen}
            groupId={props.groupId}
            onClose={props.onClose}
            initialValues={createInitialValuesUtil(licenseCode)}
        />
    );
};

export default GenerateQRCodeDialogWrapper;
